import { Injectable, inject } from '@angular/core';
import { StorageService } from './storage.service';

/**
 * Generic device-local "unseen changes" tracker, namespaced per entity type
 * (e.g. `'log'`, `'showreport'`).
 *
 * An item is "unseen" when its latest activity timestamp is newer than the last
 * time the user opened it — or, if never opened, newer than the namespace
 * baseline. Namespaces in {@link BASELINE_NAMESPACES} get a baseline set to
 * "now" on first use, so a large existing history (years of logs/showreports)
 * doesn't all flag at once — only changes made after adoption surface.
 *
 * Changes made by the current user can be excluded via `actorId` (you don't
 * need to be told about your own edit). State is device-local (localStorage),
 * so seeing an item on one device won't clear it on another.
 */
@Injectable({ providedIn: 'root' })
export class ReadStateService {

    /** Namespaces that only surface activity newer than a first-use baseline. */
    private static readonly BASELINE_NAMESPACES = new Set<string>(['log', 'showreport']);

    /**
     * Baselines stored before this instant are treated as unset and recomputed
     * to "now". While the log/showreport markers are admin-only this sits in the
     * past, so it has no effect. BUMP IT TO THE ROLLOUT DATE when the feature
     * goes live for everyone: it wipes any baselines created during the test
     * phase, so nobody is greeted with a backlog of test-period markers.
     */
    // Kept far in the past during the admin-only test phase so it's a no-op and
    // doesn't reset a manually-set old test baseline (e.g. 2 years ago). At
    // rollout, BUMP THIS TO THE ROLLOUT DATE to wipe any test-phase baselines.
    private static readonly BASELINE_EPOCH_MS = Date.UTC(2020, 0, 1); // pre-rollout: no-op

    private readonly storage = inject(StorageService);
    private readonly cache = new Map<string, { seen: Record<string, number>; baseline: number | null }>();

    /**
     * True when the item has activity the user hasn't seen.
     * @param namespace   entity type, e.g. 'log' or 'showreport'
     * @param id          item id
     * @param activityMs  latest activity time in epoch ms (e.g. from updatedAt)
     * @param actorId     who caused the activity (updatedBy) — their own change never counts
     * @param currentUserId the viewing user
     */
    public isUnseen(namespace: string, id: number | string | null | undefined, activityMs: number,
                    actorId?: number | null, currentUserId?: number | null): boolean {
        if (id == null || !activityMs) {
            return false;
        }
        if (actorId != null && currentUserId != null && actorId === currentUserId) {
            return false;
        }
        const state = this.state(namespace);
        const floor = state.seen[String(id)] ?? state.baseline ?? 0;
        return activityMs > floor;
    }

    /** Record that the user has seen this item's current state (clears "unseen"). */
    public markSeen(namespace: string, id: number | string | null | undefined, activityMs: number): void {
        if (id == null) {
            return;
        }
        const state = this.state(namespace);
        state.seen[String(id)] = Math.max(activityMs || 0, Date.now());
        this.storage.saveLocalData(this.seenKey(namespace), state.seen);
    }

    /** Convert a date-ish value to epoch ms (0 when absent/invalid). */
    public toMs(value: string | Date | null | undefined): number {
        if (!value) {
            return 0;
        }
        const ms = new Date(value).getTime();
        return isNaN(ms) ? 0 : ms;
    }

    private state(namespace: string): { seen: Record<string, number>; baseline: number | null } {
        let state = this.cache.get(namespace);
        if (!state) {
            const seen = this.storage.getLocalData(this.seenKey(namespace)) ?? {};
            let baseline: number | null = null;
            if (ReadStateService.BASELINE_NAMESPACES.has(namespace)) {
                const stored = this.storage.getLocalData(this.baselineKey(namespace));
                if (typeof stored === 'number' && stored >= ReadStateService.BASELINE_EPOCH_MS) {
                    baseline = stored;
                } else {
                    // Missing, or stored before the epoch (e.g. a test-phase baseline
                    // after the epoch was bumped at rollout) → start fresh from now.
                    baseline = Date.now();
                    this.storage.saveLocalData(this.baselineKey(namespace), baseline);
                }
            }
            state = { seen, baseline };
            this.cache.set(namespace, state);
        }
        return state;
    }

    private seenKey(namespace: string): string {
        return `read-state:${namespace}:seen`;
    }

    private baselineKey(namespace: string): string {
        return `read-state:${namespace}:baseline`;
    }
}
